/**
 * PromoterDashboardRewards.tsx - Promoter Rewards Overview Component
 *
 * This component displays an overview of available rewards and the promoter's
 * progress toward achieving them with both amount-based and rank-based systems.
 *
 * Key Features:
 * - Overview of all available reward systems
 * - Promoter's current reward eligibility status
 * - Progress indicators for reward achievement
 * - Achievement celebration and recognition
 * - Reward tier visualization and requirements
 * - Motivation messaging for next reward levels
 *
 * Reward Systems Display:
 * - Amount-based rewards with progress tracking
 * - Rank-based rewards with position requirements
 * - Achievement status and completion indicators
 * - Next reward targets and requirements
 * - Reward descriptions and value information
 *
 * Progress Tracking:
 * - Visual progress bars for reward tiers
 * - Achievement status indicators
 * - Distance to next reward level
 * - Historical achievement timeline
 * - Celebration of completed rewards
 *
 * Motivation Features:
 * - Clear next steps for reward achievement
 * - Progress encouragement and messaging
 * - Achievement celebrations and recognition
 * - Competitive elements and peer comparison
 *
 * Props:
 * - promoterId: string - ID of promoter for reward tracking
 *
 * Dependencies: Reward components, MobX store, achievement calculations
 */

import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { Trophy, UserPlus } from "lucide-react";
import { fundraiserStore } from "../../stores/FundraiserStore";
import { PromoterDashboardRewardsByAmount } from "./PromoterDashboardRewardsByAmount";
import { PromoterDashboardRewardsByRank } from "./PromoterDashboardRewardsByRank";
import { CreateFundraiserModel, PromoterProgressModel } from "../../models";
import { NavigateFunction } from "react-router-dom";
import {
  AuthService,
  Loader,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import { firstValueFrom } from "rxjs";
import { FundraiserService } from "../../services";

const AchievementsContainer = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 32px;
  margin: 32px 0;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.3);
`;

const AchievementsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const HeaderCenter = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
`;

const AchievementsTitle = styled.h3`
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
`;

const TrophyIcon = styled.div`
  color: #f59e0b;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SegmentControl = styled.div`
  display: flex;
  background: #f8fafc;
  border-radius: 12px;
  padding: 4px;
  gap: 4px;
`;

const SegmentTab = styled.button<{ active: boolean }>`
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  ${(props) =>
    props.active
      ? `
    background: #142937;
    color: #ffffff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  `
      : `
    background: transparent;
    color: #64748b;
    
    &:hover {
      color: #1e293b;
      background: rgba(255, 255, 255, 0.5);
    }
  `}
`;

const HeaderRight = styled.div<{ visible: boolean }>`
  display: ${(props) => (props.visible ? "flex" : "none")};
  align-items: center;
  gap: 8px;
  max-width: 500px;
  text-align: left;
`;

const InviteText = styled.p`
  font-size: 15px;
  color: #555555;
  margin: 0;
  line-height: 1.4;
  display: flex;
  flex-direction: column;
  gap: 0;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 8px;
  border-radius: 8px;

  &:hover {
    background: rgba(16, 185, 129, 0.05);
    transform: translateY(-1px);
  }
`;

const InviteTextRow = styled.span`
  font-weight: 500;
  color: #555555;
  display: flex;
  align-items: center;
  gap: 4px;
  line-height: 1.4;
`;

const InviteTextGreen = styled.span`
  font-weight: 700;
  color: #10b981;
`;

interface PromoterDashboardRewardsProps {
  promoterId: string;
  fundraiser: CreateFundraiserModel;
  navigate: NavigateFunction;
}

const PromoterDashboardRewards: React.FC<PromoterDashboardRewardsProps> =
  observer(({ promoterId, fundraiser, navigate }) => {
    const [activeTab, setActiveTab] = useState<"amount" | "rank" | "level">(
      "amount"
    );

    const promoter = fundraiser.leaderboard
      ?.map((p) => (p.id === promoterId ? p : null))
      .find(Boolean);
    console.log(fundraiser, promoterId, promoter);

    if (!fundraiser || !promoter) {
      return null;
    }

    // Get rewards and ranks from fundraiser
    const rewards = (fundraiser.rewards || [])
      .slice()
      .sort((a, b) => a.level - b.level);

    const ranks = (fundraiser.ranks || []).slice().sort((a, b) => {
      const aRank =
        typeof a.rank === "string" ? parseInt(a.rank.split("-")[0]) : a.rank;
      const bRank =
        typeof b.rank === "string" ? parseInt(b.rank.split("-")[0]) : b.rank;
      return (aRank || 0) - (bRank || 0);
    });

    // Check if we have both types of rewards to show segment control
    const hasBothRewardTypes = rewards.length > 0 && ranks.length > 0;

    // If no rewards, hide section completely
    if (rewards.length === 0 && ranks.length === 0) {
      return null;
    }

    // Check if invite button is visible in header by checking if promoter is active
    const isPromoterActive = promoter.isActive;

    // Check if all rewards are met by this promoter
    const allRewardsEarned = () => {
      if (rewards.length === 0 && ranks.length === 0) return false;

      if (activeTab === "amount") {
        return (
          rewards.length > 0 &&
          rewards.every((reward) => reward.level <= promoter.totalRaisedAmount)
        );
      }

      if (activeTab === "rank") {
        // Get all promoters and calculate current promoter rank
        const allPromoters = fundraiser.leaderboard || [];
        const sortedPromoters = allPromoters
          .slice()
          .sort((a, b) => b.totalRaisedAmount - a.totalRaisedAmount)
          .map((p, index) => ({ ...p, rank: index + 1 }));

        const currentPromoterRank =
          sortedPromoters.find((p) => p.id === promoterId)?.rank || 1;

        // Check if current promoter qualifies for any rank reward
        for (const rank of ranks) {
          const rankRange = rank.rank.toString();
          const rangeMatch = rankRange.match(/^(\d+)-(\d+)$/);
          const singleMatch = rankRange.match(/^(\d+)$/);

          if (singleMatch) {
            const targetRank = parseInt(singleMatch[1]);
            if (currentPromoterRank <= targetRank) return true;
          } else if (rangeMatch) {
            const start = parseInt(rangeMatch[1]);
            const end = parseInt(rangeMatch[2]);
            if (currentPromoterRank >= start && currentPromoterRank <= end)
              return true;
          }
        }
        return false;
      }

      return false;
    };

    const showInviteCaption =
      isPromoterActive &&
      fundraiser?.allowPromoterInvites !== false &&
      fundraiser.includeRecruitedActivity !== false;

    const handleInviteClick = () => {
      fundraiserStore.setSelectedFundraiser(fundraiser.id);
      navigate(`/fundraiser/promoter-invite`);
    };

    return (
      <AchievementsContainer>
        <AchievementsHeader>
          <HeaderLeft>
            <TrophyIcon>
              <Trophy size={24} />
            </TrophyIcon>
            <AchievementsTitle>Rewards</AchievementsTitle>
          </HeaderLeft>
          {hasBothRewardTypes && (
            <HeaderCenter>
              <SegmentControl>
                <SegmentTab
                  active={activeTab === "amount"}
                  onClick={() => setActiveTab("amount")}
                >
                  By Amount
                </SegmentTab>
                <SegmentTab
                  active={activeTab === "rank"}
                  onClick={() => setActiveTab("rank")}
                >
                  By Rank
                </SegmentTab>
              </SegmentControl>
            </HeaderCenter>
          )}
          <HeaderRight visible={true}>
            <InviteText onClick={handleInviteClick}>
              <InviteTextRow>
                <UserPlus size={16} color="#10b981" />
                <InviteTextGreen>Invite</InviteTextGreen>
                promoters to
              </InviteTextRow>
              <InviteTextRow>help you earn rewards.</InviteTextRow>
            </InviteText>
          </HeaderRight>
        </AchievementsHeader>

        {/* Global CSS for mobile header */}
        <style>
          {`
          @media (max-width: 768px) {
            .achievements-header-desktop {
              display: none !important;
            }
            
            .mobile-achievements-header {
              display: flex !important;
              flex-direction: column;
              gap: 16px;
              margin-bottom: 32px;
            }
            
            .mobile-achievements-top-row {
              display: flex !important;
              align-items: center;
              justify-content: center;
              width: 100%;
              text-align: center;
            }
            
            .mobile-achievements-title-section {
              display: flex !important;
              align-items: center;
              gap: 12px;
            }
            
            .mobile-achievements-invite-row {
              display: flex !important;
              justify-content: center;
              width: 100%;
              text-align: center;
            }
            
            .mobile-achievements-bottom-row {
              display: flex !important;
              justify-content: center;
              width: 100%;
            }
          }
        `}
        </style>

        {/* Mobile Header - visible only on mobile */}
        <div className="mobile-achievements-header" style={{ display: "none" }}>
          <div className="mobile-achievements-top-row">
            <div className="mobile-achievements-title-section">
              <div
                style={{
                  color: "#f59e0b",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Trophy size={24} />
              </div>
              <h3
                style={{
                  fontSize: "22px",
                  fontWeight: "700",
                  color: "#1e293b",
                  margin: "0",
                }}
              >
                Rewards
              </h3>
            </div>
          </div>

          <div className="mobile-achievements-invite-row">
            <div
              onClick={handleInviteClick}
              style={{
                fontSize: "15px",
                color: "#555555",
                margin: "0",
                lineHeight: "1.4",
                display: "flex",
                flexDirection: "column",
                gap: "0",
                textAlign: "center",
                cursor: "pointer",
                padding: "8px",
                borderRadius: "8px",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(16, 185, 129, 0.05)";
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              <span
                style={{
                  fontWeight: "500",
                  color: "#555555",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  lineHeight: "1.4",
                  justifyContent: "center",
                }}
              >
                {/* <img
                  src="/user-plus.svg"
                  alt="Invite"
                  width={16}
                  height={16}
                  style={{
                    filter:
                      "brightness(0) saturate(100%) invert(44%) sepia(93%) saturate(588%) hue-rotate(119deg) brightness(98%) contrast(86%)",
                  }}
                /> */}
                <UserPlus size={16} />
                <span style={{ fontWeight: "700", color: "#10b981" }}>
                  Invite
                </span>
                promoters to
              </span>
              <span
                style={{
                  fontWeight: "500",
                  color: "#555555",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  lineHeight: "1.4",
                  justifyContent: "center",
                }}
              >
                help you earn rewards.
              </span>
            </div>
          </div>

          {hasBothRewardTypes && (
            <div className="mobile-achievements-bottom-row">
              <div
                style={{
                  display: "flex",
                  background: "#f8fafc",
                  borderRadius: "12px",
                  padding: "4px",
                  gap: "4px",
                }}
              >
                <button
                  style={{
                    padding: "10px 16px",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    background:
                      activeTab === "level" ? "#142937" : "transparent",
                    color: activeTab === "level" ? "#ffffff" : "#64748b",
                    boxShadow:
                      activeTab === "level"
                        ? "0 2px 4px rgba(0, 0, 0, 0.1)"
                        : "none",
                  }}
                  onClick={() => setActiveTab("amount")}
                >
                  By Amount
                </button>
                <button
                  style={{
                    padding: "10px 16px",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    background:
                      activeTab === "rank" ? "#142937" : "transparent",
                    color: activeTab === "rank" ? "#ffffff" : "#64748b",
                    boxShadow:
                      activeTab === "rank"
                        ? "0 2px 4px rgba(0, 0, 0, 0.1)"
                        : "none",
                  }}
                  onClick={() => setActiveTab("rank")}
                >
                  By Rank
                </button>
              </div>
            </div>
          )}
        </div>

        {activeTab === "amount" && rewards.length > 0 && (
          <PromoterDashboardRewardsByAmount
            promoterId={promoterId}
            fundraiserId={fundraiser.id}
            rewards={rewards}
            promoter={promoter}
          />
        )}

        {activeTab === "rank" && ranks.length > 0 && (
          <PromoterDashboardRewardsByRank
            promoterId={promoterId}
            fundraiserId={fundraiser.id}
            ranks={ranks}
            promoter={promoter}
            promoters={fundraiser.leaderboard || []}
          />
        )}
      </AchievementsContainer>
    );
  });
export default withRouter(PromoterDashboardRewards);
